<template>
  <div class="rooms">
    <SidebarLayout2
      :breadcrumb="breadcrumb"
      :rootPath="rootPath"
      :pageTitle="pageTitle"
      class="rooms--sidebar"
    >
      <!-- BARRA LATERAL -->
      <template #sidebar class="sidebar">
        <!-- PAINEL AZUL -->
        <transition name="fade">
          <InfoPanel height="155">
            <template #content>
              <PmwHeading
                class="sidebar--title mb-0"
                size="paragraph-80"
                weight="weight-medium"
                >{{ hotelName }}</PmwHeading
              >
            </template>
          </InfoPanel>
        </transition>
        <!-- AÇÕES DA PÁGINA -->
        <SidebarBlock class="page-actions">
          <!-- MENU DOS HOTÉIS -->
          <PmwHotelNav
            :links="menuLinks"
            :hide="isHotelAbsolute ? ['diferenciais', 'banners'] : ['banners']"
            active="quartos"
            class="menu-nav"
          />

          <PmwHeading
            class="sidebar--title"
            size="paragraph-80"
            weight="weight-medium"
            >Ações da página</PmwHeading
          >
          <!-- CADASTRAR -->
          <Button
            :hasIcon="true"
            :link="`/hoteis/quartos/${hotelId}/novo`"
            element="router-link"
            class="sidebar--button"
          >
            Criar quarto
          </Button>
          <!-- EDITAR -->
          <Button
            :link="
              selectedRooms.length === 1
                ? `/hoteis/quartos/${hotelId}/editar/${selectedRooms[0].codigo}`
                : '/'
            "
            :disabled="selectedRooms.length === 1 ? false : true"
            element="router-link"
            class="sidebar--button"
          >
            Editar
          </Button>
          <!-- DESATIVAR -->
          <Button
            v-if="allSelectedRoomsInactived()"
            :disabled="allSelectedRoomsActived()"
            variation="error"
            class="sidebar--button"
            @click="showAlertModalInactivate = true"
          >
            Desativar
          </Button>
          <!-- ATIVAR -->
          <Button
            v-if="selectedRooms.length && !allSelectedRoomsInactived()"
            :disabled="allSelectedRoomsInactived()"
            class="sidebar--button"
            @click="showAlertModalActivate = true"
          >
            Ativar
          </Button>
        </SidebarBlock>
        <!-- DICAS -->
        <SidebarBlock class="advice">
          <PmwHeading
            class="sidebar--title"
            size="paragraph-80"
            weight="weight-medium"
            >Dica</PmwHeading
          >
          <PmwParagraph class="sidebar--text">
            Aqui você poderá criar e editar as categorias de quartos que estarão
            disponíveis para venda no Letsbook.
          </PmwParagraph>
        </SidebarBlock>
      </template>

      <!-- CONTEÚDO -->
      <template #content>
        <SearchInput
          v-model="searchTerm"
          placeholder="Buscar quarto"
          class="search-term"
          @input="searchData"
        />
        <FilterDropdown
          v-model="status"
          :options="statusList"
          :hasSearch="true"
          :toggleAll="true"
          :radioOptions="false"
          toggleAllLabel="Todas"
          checkboxNoSelectionLabel="Todas"
          label="Status"
          searchFieldPlaceholder="Pesquisar status"
          class="filter-status"
          @input="searchData"
        />
        <DataTable
          :cols="gridColumns"
          :rows="rows"
          :selectableRow="true"
          :isLoading="isLoadingTableContent"
          :colsMinWidths="colsWidths"
          :selectedRowHasBgColor="true"
          class="datatable"
          ref="datatable"
          notFoundMessage="Não há quartos com a busca."
          @getSelectedRows="manageSelectedRooms"
        >
          <template v-slot:status="slotProps">
            <span>{{
              slotProps.rowDataObj.status === true ? 'Ativo' : 'Inativo'
            }}</span>
          </template>
          <template v-slot:editar="slotProps">
            <router-link
              :to="
                `/hoteis/quartos/${hotelId}/editar/${slotProps.rowDataObj.codigo}`
              "
              tag="span"
              class="links links--edit"
            >
              Editar
            </router-link>
          </template>
          <template v-slot:excluir="slotProps">
            <!-- DESATIVAR -->
            <span
              v-if="slotProps.rowDataObj.status === true"
              class="links links--delete"
              @click.stop.prevent="inactiveRoom(slotProps.rowDataObj.codigo)"
            >
              Desativar
            </span>
            <!-- ATIVAR -->
            <span
              v-else
              class="links links--edit"
              @click.stop.prevent="activeRoom(slotProps.rowDataObj.codigo)"
            >
              Ativar
            </span>
          </template>
        </DataTable>
        <!-- PAGINAÇÃO -->
        <PmwPagination
          :currentPage="pagination.currentPage"
          :items="roomsFiltered"
          :itemsPerPage="pagination.itemsPerPage"
          :itemsPerPageOptions="pagination.itemsPerPageOptions"
          :totalItems="roomsFiltered.length"
          @changePagination="changePagination"
        />
        <!-- MODAL DE INATIVAÇÃO DE QUARTO -->
        <PmwAlertModal
          v-model="showAlertModalInactivate"
          :closeButton="true"
          title="Atenção"
          cancelButtonColor="blue-100"
          confirmButtonColor="blue-100"
          icon="alert"
          confirmButtonText="Sim, quero desativar"
          cancelButtonText="Não, Cancelar"
          class="pmw-alert-modal"
          @alert-modal-confirm="inactiveRooms"
          @alert-modal-cancel="cancelModal"
        >
          <template #text>
            Ao desativar esse quarto, ele estará indisponível para venda no
            Letsbook. Você tem certeza que quer fazer isso?
          </template>
        </PmwAlertModal>
        <!-- MODAL DE ATIVAÇÃO DE QUARTO -->
        <PmwAlertModal
          v-model="showAlertModalActivate"
          :closeButton="true"
          title="Atenção"
          cancelButtonColor="blue-100"
          confirmButtonColor="blue-100"
          icon="alert"
          confirmButtonText="Sim, quero ativar"
          cancelButtonText="Não, Cancelar"
          class="pmw-alert-modal"
          @alert-modal-confirm="activeRooms"
          @alert-modal-cancel="cancelModal"
        >
          <template #text>
            Ao reativar esse quarto, ele estará disponível para venda no
            Letsbook. Você tem certeza que quer fazer isso?
          </template>
        </PmwAlertModal>
        <!-- MODAL DE ALERTA DE EXCLUSÃO DO QUARTO -->
        <PmwAlertModal
          v-model="showAlertModalRoomCantDelete"
          title="Atenção"
          cancelButtonColor="blue-100"
          confirmButtonColor="blue-100"
          confirmButtonText="Ok, entendi"
          cancelButtonText="Fechar"
          class="pmw-alert-modal"
          @alert-modal-confirm="showAlertModalRoomCantDelete = false"
          @alert-modal-cancel="showAlertModalRoomCantDelete = false"
        >
          <template #text>
            Houve um erro ao executar a ação.
          </template>
        </PmwAlertModal>
      </template>
    </SidebarLayout2>
  </div>
</template>

<script>
import SidebarLayout2 from '@/layouts/SidebarLayout2'
import { capitalizeString } from '@/helpers/string'
import SidebarBlock from '@solucoes/components-lib-tmp/src/components/patterns/SidebarBlock'
import InfoPanel from '@solucoes/components-lib-tmp/src/components/patterns/InfoPanel'
import PmwHeading from '@solucoes/components-lib-tmp/src/components/elements/PmwHeading'
import PmwParagraph from '@solucoes/components-lib-tmp/src/components/elements/PmwParagraph'
import Button from '@solucoes/components-lib-tmp/src/components/elements/Button'
import SearchInput from '@solucoes/components-lib-tmp/src/components/elements/SearchInput'
import FilterDropdown from '@solucoes/components-lib-tmp/src/components/patterns/FilterDropdown'
import DataTable from '@solucoes/components-lib-tmp/src/components/patterns/DataTable'
import PmwAlertModal from '@solucoes/components-lib-tmp/src/components/patterns/PmwAlertModal'
import PmwPagination from '@solucoes/components-lib-tmp/src/components/patterns/PmwPagination'

import hotelCommonsMixin from '@/mixins/hotelCommonsMixin'

export default {
  name: 'RoomsList',
  mixins: [hotelCommonsMixin],
  components: {
    SidebarLayout2,
    SidebarBlock,
    InfoPanel,
    PmwHeading,
    PmwParagraph,
    Button,
    SearchInput,
    FilterDropdown,
    DataTable,
    PmwAlertModal,
    PmwPagination
  },
  data() {
    return {
      /** Nome do hotel */
      hotelName: null,
      /** Configuração da página */
      pageTitle: 'Listagem de quartos',
      /** Caminhos da página */
      rootPath: {
        /** ponto de partida do breadcrumb  */
        label: 'Home',
        url: 'relatorio-conversao',
        isSpa: false /** se deve ser um link para o site novo ou antigo */
      },
      /** Caminho de pão */
      breadcrumb: [],
      /** Termo para pesquisa */
      searchTerm: null,
      /** Filtrar por status */
      status: [
        {
          value: true,
          label: 'Ativo'
        }
      ],
      /** Lista de status */
      statusList: [
        {
          value: true,
          label: 'Ativo'
        },
        {
          value: false,
          label: 'Inativo'
        }
      ],
      /** Colunas da tabela */
      gridColumns: [
        { name: 'codigo', alias: 'Código' },
        { name: 'quarto', alias: 'Quarto' },
        { name: 'exclusivoCentralReservas', alias: 'Exclusivo Central' },
        { name: 'status', alias: 'Status' },
        { name: 'editar', alias: 'Ações' },
        { name: 'excluir' }
      ],
      /** Linhas paginadas */
      rows: [],
      /** Dados para paginação */
      pagination: {
        currentPage: 1,
        itemsPerPage: 25,
        itemsPerPageOptions: [
          {
            text: '25',
            value: 25
          },
          {
            text: '50',
            value: 50
          },
          {
            text: '75',
            value: 75
          },
          {
            text: '100',
            value: 100
          }
        ]
      },
      /** Larguras das colunas */
      colsWidths: {
        codigo: '80px',
        status: '80px',
        editar: '80px',
        excluir: '80px'
      },
      /** Quartos selecionados */
      selectedRooms: [],
      /** Quarto selecionado */
      selectedRoom: null,
      /** Loader da tabela */
      isLoadingTableContent: true,
      /** Lista de quartos */
      rooms: [],
      /** Lista de quartos filtrada */
      roomsFiltered: [],
      /** Modal de desativação de quarto(s) */
      showAlertModalInactivate: false,
      /** Modal de ativação de quarto(s) */
      showAlertModalActivate: false,
      /** Mostar modal de alerta caso o quarto não possa ser excluído */
      showAlertModalRoomCantDelete: false,
      isHotelAbsolute: false
    }
  },
  created() {
    /** Pegar o hotel */
    this.hotelId = this.$route.params.idHotel
    this.getHotel()
    /** Listar os quartos */
    this.getRooms()
  },
  methods: {
    /** Buscar hotel */
    getHotel() {
      this.$hotel
        .get(this.hotelId)
        .then((response) => {
          /** Atualizando o nome do hotel */
          this.hotelName = response.data.nome
          this.isHotelAbsolute = response.data.tarifarioAbsoluto

          /** Caminho de pão */
          this.breadcrumb = [
            {
              label: 'Letsbook',
              url: '',
              isSpa: false
            },
            {
              label: 'Hotéis',
              url: '/hoteis',
              isSpa: true
            },
            {
              label: capitalizeString(this.hotelName),
              url: `/hoteis/editar/${this.hotelId}`,
              isSpa: true
            },
            {
              label: 'Quartos',
              url: `/hoteis/quartos/${this.$route.params.idHotel}`,
              isSpa: true
            }
          ]
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Houve um erro ao buscar o hotel.'
          })
          /** Redirecionar para a listagem de hoteis */
          setTimeout(() => {
            window.location = '/hoteis'
          }, 1000)
        })
    },
    /** Buscar quartos */
    getRooms() {
      this.$room
        .get(this.hotelId)
        .then((response) => {
          /** Separar por quartos */
          this.rooms = response.data.map((room) => {
            const item = {
              data: {
                codigo: room.codigo,
                quarto: room.nome,
                exclusivoCentralReservas: room.exclusivoCentralReservas
                  ? 'Sim'
                  : 'Não',
                status: room.ativo
              }
            }
            return item
          })
          this.isLoadingTableContent = false
          /** Filtrar dados */
          this.searchData()
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Houve um erro ao apresentar a listagem de quartos.'
          })
          /** Redirecionar para a listagem de hoteis */
          setTimeout(() => {
            window.location = '/hoteis'
          }, 1000)
        })
    },
    /** Selecionar os quartos
     * @param {Array} values - Quartos selecionados
     */
    manageSelectedRooms(values) {
      this.selectedRooms = values
    },
    /** Pesquisar quartos */
    searchData() {
      /** Pesquisa pelo nome do quarto */
      const nameSearch = this.searchTerm ? this.searchTerm.toLowerCase() : false
      /** Pesquisa pelo status do quarto */
      const statusSearch = this.status.map((item) => item.value)
      /** Pegar os quartos filtrados */
      this.roomsFiltered = this.rooms.filter(function(item) {
        /** Verificar se o nome é igual ao termo pesquisado */
        const searchName =
          !nameSearch || item.data.quarto.toLowerCase().includes(nameSearch)
            ? true
            : false
        /** Verificar se o status é igual ao status filtrado */
        const searchStatus =
          statusSearch.length === 0 || statusSearch.includes(item.data.status)
            ? true
            : false
        return searchName && searchStatus
      })
    },
    /** Paginar
     * @param {Object} childData - dados da paginação que são retornados pelo componente
     */
    changePagination(childData) {
      /** Dados da tabela conforme a paginação */
      this.pagination.currentPage = childData.currentPage
      this.rows = childData.items
    },
    /** Desativar quarto
     * @param {Integer} id - Id do quarto
     */
    inactiveRoom(id) {
      this.selectedRoom = id
      this.showAlertModalInactivate = true
    },
    /** Ativar quarto
     * @param {Integer} id - Id do quarto
     */
    activeRoom(id) {
      this.selectedRoom = id
      this.showAlertModalActivate = true
    },
    /** Cancelar inativação/ativação dos quartos */
    cancelModal() {
      this.showAlertModalInactivate = false
      this.showAlertModalActivate = false
      this.selectedRooms = []
      this.selectedRoom = null
      /** Desmarcar itens da tabela */
      this.$refs['datatable'].selectAllRows(false)
    },
    /** Desativar quartos */
    inactiveRooms() {
      /** Quartos selecionados */
      let rooms = []
      if (this.selectedRoom === null) {
        rooms = this.selectedRooms.map((item) => item.codigo)
      } else {
        rooms = [this.selectedRoom]
      }
      this.$room
        .inative(parseInt(this.hotelId), rooms)
        .then(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Quarto(s) desativado(s) com sucesso.'
          })
        })
        .catch(() => {
          this.showAlertModalRoomCantDelete = true
        })
        .finally(() => {
          /** Resetar tabela */
          this.resetRooms()
        })
    },
    /** Ativar quartos */
    activeRooms() {
      /** Quartos selecionados */
      let rooms = []
      if (this.selectedRoom === null) {
        rooms = this.selectedRooms.map((item) => item.codigo)
      } else {
        rooms = [this.selectedRoom]
      }
      this.$room
        .active(parseInt(this.hotelId), rooms)
        .then(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Quarto(s) ativado(s) com sucesso.'
          })
        })
        .catch(() => {
          this.showAlertModalRoomCantDelete = true
        })
        .finally(() => {
          /** Resetar tabela */
          this.resetRooms()
        })
    },
    /** Resetar os dados da tabela */
    resetRooms() {
      this.showAlertModalInactivate = false
      this.selectedRoom = null
      this.selectedRooms = []
      this.getRooms()
    },
    /** Se todos os itens selecionados estão ativados */
    allSelectedRoomsActived() {
      return this.selectedRooms.length === 0 ||
        this.selectedRooms.filter((item) => item.status === true).length <
          this.selectedRooms.length
        ? true
        : false
    },
    /** Se todos os itens selecionados estão desativos */
    allSelectedRoomsInactived() {
      return this.selectedRooms.length === 0 ||
        this.selectedRooms.filter((item) => item.status === false).length <
          this.selectedRooms.length
        ? true
        : false
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .lb-breadcrumb--link:nth-child(2) {
  cursor: default;
  pointer-events: none;
}
.sidebar {
  .menu-nav {
    max-height: calc(100vh - 415px);
    overflow-y: scroll;
  }
  &--title {
    margin-bottom: 15px;
  }
  &--button {
    margin-bottom: 10px;
  }
  &--text {
    line-height: 21px;
  }
}

.search-term {
  max-width: 215px;
  margin-bottom: 20px;
}

.filter-status {
  margin-bottom: 20px;
  min-width: 190px;
  max-width: 215px;
  ::v-deep > .pmw-dropdown--trigger-button {
    gap: 5px;
  }
}

.datatable {
  max-height: initial;
  a {
    cursor: pointer;
  }
  ::v-deep {
    .pmw-td span {
      font-size: font-size('size-small');
      line-height: font-size('size-small');
      max-height: font-size('size-small');
    }
    .no-gutters > .pmw-col {
      padding-left: 13px;
      padding-right: 13px;
    }

    /** Largura - Código */
    .pmw-col.pmw-th:nth-child(2),
    .pmw-col.pmw-td:nth-child(2) {
      max-width: 80px;
    }

    /** Largura - Status */
    .pmw-col.pmw-th:nth-child(5),
    .pmw-col.pmw-td:nth-child(5) {
      max-width: 80px;
    }

    /** Largura - Editar */
    .pmw-col.pmw-th:nth-child(6),
    .pmw-col.pmw-td:nth-child(6) {
      max-width: 80px;
    }

    /** Largura - Remover */
    .pmw-col.pmw-th:nth-child(7),
    .pmw-col.pmw-td:nth-child(7) {
      max-width: 80px;
    }

    /** Largura - Status */
    .pmw-col.pmw-th:nth-child(5),
    .pmw-col.pmw-td:nth-child(5) {
      max-width: 80px;
    }

    .pmw-col.pmw-th:nth-child(6) {
      border-right: none;
    }

    .pmw-col.pmw-th:nth-child(6),
    .pmw-col.pmw-th:nth-child(7) {
      pointer-events: none;
      .pmw-icon {
        display: none;
      }
    }

    .links {
      font-size: font-size('size-small');
      font-weight: 500;
      cursor: pointer;

      &--edit {
        color: pmw-colors('blue-100');
      }
      &--delete {
        color: pmw-colors('error-100');
      }
    }
  }
}
</style>
